import type { AggregationMethod } from "../utils/traceConstants"
import { MAX_TRACE_AGENT_SCOPE_IDS } from "./customTraceAgentScope"

export interface CustomTracePreviewData {
  matchedSpanCount: number
  sampleValues: string[]
  isNumeric: boolean
  isBoolean: boolean
  latestTimestamp: string | null
}

interface CustomTracePreviewProps {
  aggregationMethod?: AggregationMethod
  currentSpanName: string
  hasMetricAttribute: boolean
  hasTooManyTraceAgentIds: boolean
  isDuration: boolean
  preview: CustomTracePreviewData | undefined
  previewIsError: boolean
  previewIsPending: boolean
  previewRequiresNumericAttribute: boolean
}

function formatPreviewTimestamp(timestamp: string | null): string {
  if (!timestamp) {
    return "None"
  }

  const parsed = new Date(timestamp)
  return Number.isNaN(parsed.getTime()) ? timestamp : parsed.toLocaleString()
}

export function CustomTracePreview({
  aggregationMethod,
  currentSpanName,
  hasMetricAttribute,
  hasTooManyTraceAgentIds,
  isDuration,
  preview,
  previewIsError,
  previewIsPending,
  previewRequiresNumericAttribute,
}: CustomTracePreviewProps) {
  return (
    <div
      className="rounded-md border border-coval-border bg-surface-1 p-3 text-sm"
      data-testid="custom-trace-preview"
    >
      <div className="mb-2 font-medium text-coval-text">Preview</div>
      {hasTooManyTraceAgentIds ? (
        <div className="text-coval-danger-text">
          Custom trace preview and suggestions support at most{" "}
          {MAX_TRACE_AGENT_SCOPE_IDS} selected agents, so both are off. Reduce
          the agent scope to preview this metric. You can still type a span and
          attribute and save.
        </div>
      ) : !currentSpanName ? (
        <div className="text-coval-text-muted">Select a span.</div>
      ) : previewIsPending ? (
        <div className="text-coval-text-muted">Loading preview...</div>
      ) : previewIsError ? (
        <div className="text-coval-danger-text">
          Preview unavailable. Check the span and attribute, then try again.
        </div>
      ) : preview?.matchedSpanCount === 0 ? (
        <div className="text-coval-text-muted">No matching spans found.</div>
      ) : preview ? (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-xs text-coval-text-muted">Matched Spans</div>
              <div className="font-medium">{preview.matchedSpanCount}</div>
            </div>
            <div>
              <div className="text-xs text-coval-text-muted">Latest Match</div>
              <div className="font-medium">
                {formatPreviewTimestamp(preview.latestTimestamp)}
              </div>
            </div>
          </div>
          <div>
            <div className="text-xs text-coval-text-muted">
              Source attribute type
            </div>
            <div
              className={
                preview.isNumeric ||
                preview.isBoolean ||
                !previewRequiresNumericAttribute
                  ? "font-medium text-coval-success-text"
                  : "font-medium text-coval-danger-text"
              }
            >
              {isDuration
                ? "Span duration"
                : !hasMetricAttribute
                  ? "No attribute selected"
                  : preview.isBoolean
                    ? "Boolean"
                    : preview.isNumeric
                      ? "Numeric"
                      : previewRequiresNumericAttribute
                        ? "Non-numeric"
                        : "Text"}
            </div>
          </div>
          <div>
            <div className="text-xs text-coval-text-muted">Metric result</div>
            <div className="font-medium">
              {aggregationMethod === "values"
                ? "Values with frequencies and timed occurrences"
                : aggregationMethod === "count"
                  ? "Number of matching occurrences"
                  : "Number"}
            </div>
            {aggregationMethod !== "values" &&
              !preview.isNumeric &&
              hasMetricAttribute && (
                <div className="text-xs text-coval-text-muted">
                  Choose Values to report the attribute values themselves.
                </div>
              )}
          </div>
          {preview.sampleValues.length > 0 && (
            <div>
              <div className="text-xs text-coval-text-muted">Sample Values</div>
              <div
                className="mt-1 flex flex-wrap gap-1"
                data-dd-privacy="mask"
                data-ph-capture="false"
              >
                {preview.sampleValues.map((value, index) => (
                  <span
                    className="max-w-full truncate rounded bg-surface-1 px-2 py-1 font-mono text-xs"
                    key={`${value}-${index}`}
                  >
                    {value}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}
